import { css } from '@emotion/react';
import { useNotifications } from '@kaiizen/kai-pro-api-react-state';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChangeOrderReviewIcon,
  CloseOutReviewIcon,
  EditRequestReviewIcon,
  ScopeReviewIcon,
} from '../Icons/AllIcons';
import { formatRelativeTime } from '../Lib';
import {
  useNavigateToMarkets,
  useNavigateToProject,
  useNavigateToScan,
} from '../State/kaiNavigation';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '../Styles';
import { NotesCountBubble } from './NotesCountBubble';

const WRAPPER_STYLE = css`
  label: NOTIFICATION_BELL_WRAPPER;
  position: relative;
  height: ${LAYOUT2.topBarHeight}px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const BELL_STYLE = (lightHeader: boolean) =>
  css`
  label: NOTIFICATION_BELL;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 6px;
  transition: background 0.15s ease;
  &:hover {
    background: ${lightHeader ? 'rgba(29, 29, 29, 0.06)' : 'rgba(255, 255, 255, 0.08)'};
  }
`;

const POPOVER_STYLE = css`
  label: NOTIFICATION_POPOVER;
  position: absolute;
  top: ${LAYOUT2.topBarHeight}px;
  right: 0;
  width: 400px;
  max-height: 520px;
  display: flex;
  flex-direction: column;
  background: #1d1d1d;
  color: #ffffff;
  border: none;
  border-radius: 0;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.35);
  overflow: hidden;
  z-index: 1201;
`;

const POPOVER_HEADER_STYLE = css`
  label: NOTIFICATION_POPOVER_HEADER;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${LAYOUT2.spacing2}px ${LAYOUT2.spacing2}px;
  background: #1d1d1d;
  border-bottom: none;
  ${TYPOGRAPHY2.mediumBold}
`;

const POPOVER_TABS_STYLE = css`
  label: NOTIFICATION_POPOVER_TABS;
  flex-shrink: 0;
  display: flex;
  gap: ${LAYOUT2.spacing2}px;
  padding: 0 ${LAYOUT2.spacing2}px;
  border-bottom: 1px solid #565656;
  background: #1d1d1d;
`;

const TAB_BUTTON_STYLE = (active: boolean) =>
  css`
  label: NOTIFICATION_TAB_BUTTON;
  padding: ${LAYOUT2.spacing1p5}px 0;
  border: none;
  border-bottom: 2px solid ${active ? '#feb05d' : 'transparent'};
  color: ${active ? '#feb05d' : '#b7b7b7'};
  background: transparent;
  ${TYPOGRAPHY2.small}
  cursor: pointer;
`;

const POPOVER_BODY_STYLE = css`
  label: NOTIFICATION_POPOVER_BODY;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const ITEM_STYLE = (isUnread: boolean) =>
  css`
  label: NOTIFICATION_ITEM;
  display: flex;
  gap: ${LAYOUT2.spacing2}px;
  padding: ${LAYOUT2.spacing2}px;
  background: ${isUnread ? '#242424' : 'transparent'};
  border-bottom: 1px solid #2e2e2e;
  cursor: pointer;
  &:hover {
    background: #2a2a2a;
  }
`;

const NOTIFICATION_ICON_STYLE = css`
  label: NOTIFICATION_ICON_STYLE;
  width: 32px;
  height: 32px;
  min-width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.04);
`;

const ITEM_CONTENT_STYLE = css`
  label: NOTIFICATION_ITEM_CONTENT;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TITLE_ROW_STYLE = css`
  label: NOTIFICATION_TITLE_ROW;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${LAYOUT2.spacing1}px;
`;

const TITLE_LEFT_STYLE = css`
  label: NOTIFICATION_TITLE_LEFT;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacing1}px;
  min-width: 0;
`;

const NOTIFICATION_TITLE_STYLE = (isUnread: boolean) =>
  css`
  label: NOTIFICATION_TITLE_STYLE;
  ${TYPOGRAPHY2.smallBold}
  color: ${isUnread ? '#ffffff' : '#d6d6d6'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NOTIFICATION_TITLE_DATE_STYLE = css`
  label: NOTIFICATION_TITLE_DATE_STYLE;
  ${TYPOGRAPHY2.xSmall}
  color: #7f7f7f;
  white-space: nowrap;
`;

const NOTIFICATION_ADDRESS_STYLE = css`
  label: NOTIFICATION_ADDRESS_STYLE;
  ${TYPOGRAPHY2.small}
  color: #f0f0f0;
`;

const NOTIFICATION_BODY_ROW_STYLE = css`
  label: NOTIFICATION_BODY_ROW_STYLE;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacing1}px;
`;

const NOTIFICATION_TEXT_STYLE = css`
  label: NOTIFICATION_TEXT_STYLE;
  ${TYPOGRAPHY2.small}
  color: #b7b7b7;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const NOTIFICATION_ORG_ID_STYLE = css`
  label: NOTIFICATION_ORG_ID_STYLE;
  ${TYPOGRAPHY2.xSmall}
  color: #7f7f7f;
  white-space: normal;
  overflow-wrap: anywhere;
`;

const EMPTY_STYLE = css`
  label: NOTIFICATION_EMPTY;
  padding: ${LAYOUT2.spacing3}px;
  text-align: center;
  color: #9b9b9b;
  ${TYPOGRAPHY2.small}
`;

const READ_ALL_BUTTON_STYLE = css`
  label: NOTIFICATION_READ_ALL_BUTTON;
  ${TYPOGRAPHY2.small}
  color: #b7b7b7;
  background: none;
  border: none;
  cursor: pointer;
  &:hover {
    color: #ffffff;
  }
`;

const POPOVER_FOOTER_STYLE = css`
  label: NOTIFICATION_POPOVER_FOOTER;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  padding: ${LAYOUT2.spacing1p5}px ${LAYOUT2.spacing2}px;
  border-top: 1px solid #2e2e2e;
  background: #1d1d1d;
`;

const VIEW_MORE_BUTTON_STYLE = css`
  label: NOTIFICATION_VIEW_MORE_BUTTON;
  ${TYPOGRAPHY2.smallBold}
  color: #feb05d;
  background: none;
  border: none;
  cursor: pointer;
  padding: ${LAYOUT2.spacing1}px ${LAYOUT2.spacing2}px;
  &:hover {
    color: #ffffff;
  }
  &:disabled {
    color: #7f7f7f;
    cursor: default;
  }
`;

export const NotificationBell = ({
  lightHeader = false,
  icon,
}: {
  lightHeader?: boolean;
  icon?: ReactNode;
} = {}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all');
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const navigateToScan = useNavigateToScan();
  const navigateToProject = useNavigateToProject();
  const navigateToMarkets = useNavigateToMarkets();
  const {
    notifications,
    unreadCount,
    markRead,
    markAllRead,
    refetch,
    hasMore,
    loadMore,
  } = useNotifications({ limit: 6 });

  useEffect(() => {
    if (!open) return;
    refetch();
  }, [open, refetch]);

  useEffect(() => {
    // TODO: replace polling with Pusher realtime updates
    const intervalId = window.setInterval(() => {
      refetch();
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, [refetch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (wrapperRef.current.contains(event.target as Node)) return;
      setOpen(false);
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const sortedNotifications = useMemo(() => notifications, [notifications]);

  const filteredNotifications = useMemo(() => {
    if (filter === 'unread') {
      return sortedNotifications.filter(item => !item.readAt);
    }
    if (filter === 'read') {
      return sortedNotifications.filter(item => Boolean(item.readAt));
    }
    return sortedNotifications;
  }, [filter, sortedNotifications]);

  const getReviewIcon = (title: string) => {
    if (title === 'EDIT REQUEST REVIEW') {
      return (
        <span css={NOTIFICATION_ICON_STYLE}>
          <EditRequestReviewIcon size={20} color='#FEB05D' />
        </span>
      );
    }
    if (title === 'SCOPE REVIEW') {
      return (
        <span css={NOTIFICATION_ICON_STYLE}>
          <ScopeReviewIcon size={20} color='#8FD1FF' />
        </span>
      );
    }
    if (title === 'CHANGE ORDER REVIEW') {
      return (
        <span css={NOTIFICATION_ICON_STYLE}>
          <ChangeOrderReviewIcon size={20} color='#88D5A8' />
        </span>
      );
    }
    if (title === 'CLOSEOUT REVIEW') {
      return (
        <span css={NOTIFICATION_ICON_STYLE}>
          <CloseOutReviewIcon size={20} color='#D6B4FF' />
        </span>
      );
    }
    return (
      <span css={NOTIFICATION_ICON_STYLE}>
        {icon ?? <NotificationsNoneIcon style={{ color: '#B7B7B7' }} />}
      </span>
    );
  };

  const handleViewMore = async () => {
    if (!loadMore || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      await loadMore();
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleNotificationClick = async (notification: any) => {
    if (!notification.readAt) await markRead([notification.id]);
    setOpen(false);

    if (notification.scanId) {
      navigateToScan({
        scanId: notification.scanId,
        review: true,
      });
      return;
    }

    const projectId = notification.propertyProjectId ?? notification.projectId;
    if (projectId) {
      navigateToProject({ projectId });
      return;
    }

    const marketId = notification.orgMarketId ?? notification.marketId;
    if (marketId) {
      navigateToMarkets({ id: marketId });
    }
  };

  const getStreetOnly = (notification: any) => {
    const address = notification.address;
    if (!address) return '';
    return address.split(',')[0];
  };

  const getPropertyDisplay = (notification: any) => {
    if (notification.propertyIdLabel && notification.propertyId) {
      return `${notification.propertyIdLabel}: ${notification.propertyId}`;
    }
    return '';
  };

  const renderNotification = (notification: any) => {
    const isUnread = !notification.readAt;
    const streetOnly = getStreetOnly(notification);
    const propertyDisplay = getPropertyDisplay(notification);

    return (
      <div
        key={notification.id}
        css={ITEM_STYLE(isUnread)}
        onClick={() => handleNotificationClick(notification)}
      >
        {getReviewIcon(notification.title ?? '')}
        <div css={ITEM_CONTENT_STYLE}>
          <div css={TITLE_ROW_STYLE}>
            <div css={TITLE_LEFT_STYLE}>
              <div css={NOTIFICATION_TITLE_STYLE(isUnread)}>
                {notification.title}
              </div>
            </div>
            <div css={NOTIFICATION_TITLE_DATE_STYLE}>
              {notification.createdAt
                ? formatRelativeTime(notification.createdAt)
                : ''}
            </div>
          </div>
          {streetOnly && <div css={NOTIFICATION_ADDRESS_STYLE}>{streetOnly}</div>}
          <div css={NOTIFICATION_BODY_ROW_STYLE}>
            {notification.text && <div css={NOTIFICATION_TEXT_STYLE}>{notification.text}</div>}
          </div>
          {propertyDisplay && <div css={NOTIFICATION_ORG_ID_STYLE}>{propertyDisplay}</div>}
        </div>
      </div>
    );
  };

  return (
    <div css={WRAPPER_STYLE} ref={wrapperRef}>
      <div
        css={BELL_STYLE(lightHeader)}
        onClick={() => setOpen(!open)}
        title='Notifications'
      >
        {icon ?? (
          <NotificationsNoneIcon
            style={{
              color: lightHeader ? '#1D1D1D' : COLORS2.alwaysDark.text.white,
            }}
          />
        )}
        {unreadCount > 0 && (
          <NotesCountBubble
            count={unreadCount}
            top={4}
            right={4}
            backgroundColor={COLORS2.namedColors.red}
            textColor={COLORS2.namedColors.white}
            size='small'
          />
        )}
      </div>
      {open && (
        <div css={POPOVER_STYLE}>
          <div css={POPOVER_HEADER_STYLE}>
            <span>Notifications</span>
            {unreadCount > 0 && (
              <button css={READ_ALL_BUTTON_STYLE} onClick={() => markAllRead()}>
                Mark all read
              </button>
            )}
          </div>
          <div css={POPOVER_TABS_STYLE}>
            <button
              css={TAB_BUTTON_STYLE(filter === 'all')}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button
              css={TAB_BUTTON_STYLE(filter === 'unread')}
              onClick={() => setFilter('unread')}
            >
              Unread
            </button>
            <button
              css={TAB_BUTTON_STYLE(filter === 'read')}
              onClick={() => setFilter('read')}
            >
              Read
            </button>
          </div>
          <div css={POPOVER_BODY_STYLE}>
            {filteredNotifications.length === 0 && <div css={EMPTY_STYLE}>No notifications</div>}
            {filteredNotifications.map(renderNotification)}
          </div>
          {hasMore && filter === 'all' && (
            <div css={POPOVER_FOOTER_STYLE}>
              <button
                css={VIEW_MORE_BUTTON_STYLE}
                onClick={handleViewMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? 'Loading…' : 'View more'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
